import { create } from 'zustand';
import type { OnboardingStepConfig } from '@/services/data/onboarding';

export type OnboardingStepKey = 
  | 'knowledge_base'
  | 'shooting_setup'
  | 'decor_selection'
  | 'thumbnail_selection'
  | 'network_connection'
  | 'editorial_planning'
  | 'cross_post'
  | 'team_introduction';

interface OnboardingState {
  // Flow state
  isOnboardingActive: boolean;
  currentStepIndex: number;
  steps: OnboardingStepConfig[];
  
  // Loading states
  isLoading: boolean;
  isCompleting: boolean;
  
  // Actions
  setSteps: (steps: OnboardingStepConfig[]) => void;
  setCurrentStepIndex: (index: number) => void;
  nextStep: () => void;
  previousStep: () => void;
  startOnboarding: (steps: OnboardingStepConfig[]) => void;
  completeOnboarding: () => void;
  skipOnboarding: () => void;
  setLoading: (loading: boolean) => void;
  setCompleting: (completing: boolean) => void;
  reset: () => void;
  
  // Computed helpers
  getCurrentStep: () => OnboardingStepConfig | null;
  isFirstStep: () => boolean;
  isLastStep: () => boolean;
  getProgress: () => { current: number; total: number; percentage: number };
}

const initialState = {
  isOnboardingActive: false,
  currentStepIndex: 0,
  steps: [] as OnboardingStepConfig[],
  isLoading: true,
  isCompleting: false,
};

export const useOnboardingStore = create<OnboardingState>((set, get) => ({
  ...initialState,

  setSteps: (steps) => set({ steps }),
  
  setCurrentStepIndex: (index) => set({ currentStepIndex: index }),
  
  nextStep: () => {
    const { currentStepIndex, steps } = get();
    if (currentStepIndex < steps.length - 1) {
      set({ currentStepIndex: currentStepIndex + 1 });
    }
  },
  
  previousStep: () => {
    const { currentStepIndex } = get();
    if (currentStepIndex > 0) {
      set({ currentStepIndex: currentStepIndex - 1 });
    }
  },
  
  startOnboarding: (steps) => set({
    isOnboardingActive: true,
    steps,
    currentStepIndex: 0,
    isLoading: false,
  }),
  
  completeOnboarding: () => set({
    isOnboardingActive: false,
    isCompleting: false,
  }),
  
  skipOnboarding: () => set({
    isOnboardingActive: false,
    isCompleting: false,
  }),
  
  setLoading: (loading) => set({ isLoading: loading }),
  
  setCompleting: (completing) => set({ isCompleting: completing }),
  
  reset: () => set(initialState),
  
  // Computed helpers
  getCurrentStep: () => {
    const { steps, currentStepIndex } = get();
    return steps[currentStepIndex] || null;
  },
  
  isFirstStep: () => get().currentStepIndex === 0,
  
  isLastStep: () => {
    const { currentStepIndex, steps } = get();
    return currentStepIndex === steps.length - 1;
  },
  
  getProgress: () => {
    const { currentStepIndex, steps } = get();
    const total = steps.length;
    const current = currentStepIndex + 1;
    const percentage = total > 0 ? (current / total) * 100 : 0;
    return { current, total, percentage };
  },
}));
